// HeaderDropdown.js - Shared dropdown card primitive for the redesigned
// header (profile menu, help menu, and any future header menus).
//
// On web, an open dropdown closes itself on an outside mousedown or an
// Escape keydown. On native there is no backdrop concept; callers close the
// menu themselves when an item is selected (there is no outside-press
// detection on native, and none is added here).

import React, { useEffect, useRef } from "react";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { HEADER, menuColors } from "./headerTheme";

export default function HeaderDropdown({
  open,
  onClose,
  isDarkMode,
  align = "right",
  width = 280,
  maxHeight,
  trigger,
  testID,
  children,
}) {
  const wrapperRef = useRef(null);
  const colors = menuColors(isDarkMode);

  // Held in a ref (updated every render) so the effect below can depend on
  // just `open` — a re-render that only changes the `onClose` identity (a
  // new inline arrow from the caller) must not tear down and re-add the
  // document/window listeners.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || Platform.OS !== "web") {
      return undefined;
    }
    if (typeof document === "undefined") {
      return undefined;
    }

    const handleMouseDown = (event) => {
      const node = wrapperRef.current;
      if (node && typeof node.contains === "function" && !node.contains(event.target)) {
        onCloseRef.current();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };

    // Clicking into the Unity iframe embedded on stage blurs the window
    // without firing a document mousedown, so the menu would otherwise be
    // left open over the iframe.
    const handleBlur = () => {
      onCloseRef.current();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("blur", handleBlur);
    };
  }, [open]);

  return (
    <View ref={wrapperRef} style={styles.wrapper} testID={testID}>
      {trigger}
      {open && (
        <View
          style={[
            styles.card,
            {
              [align]: 0,
              width,
              backgroundColor: colors.bg,
              borderColor: colors.line,
            },
          ]}
          accessibilityRole="menu"
          testID={testID ? `${testID}-card` : undefined}
        >
          {maxHeight != null ? (
            <ScrollView
              style={{ maxHeight }}
              testID={testID ? `${testID}-scroll` : undefined}
            >
              {children}
            </ScrollView>
          ) : (
            children
          )}
        </View>
      )}
    </View>
  );
}

export function HeaderMenuSection({ isDarkMode, title }) {
  const colors = menuColors(isDarkMode);
  return (
    <Text style={[styles.sectionTitle, { color: colors.muted }]}>{title}</Text>
  );
}

export function HeaderMenuDivider({ isDarkMode }) {
  const colors = menuColors(isDarkMode);
  return <View style={[styles.divider, { backgroundColor: colors.line }]} />;
}

export function HeaderMenuItem({
  isDarkMode,
  icon,
  label,
  onPress,
  right,
  tag,
  danger,
  disabled,
  testID,
  accessibilityLabel,
}) {
  const colors = menuColors(isDarkMode);
  const iconColor = danger ? HEADER.danger : colors.muted;
  const textColor = danger ? HEADER.danger : colors.text;

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      testID={testID}
      accessibilityLabel={accessibilityLabel || label}
      accessibilityRole="menuitem"
      style={({ hovered }) => [
        styles.item,
        hovered && !disabled ? { backgroundColor: colors.hover } : null,
        disabled ? styles.itemDisabled : null,
      ]}
    >
      {icon && <MaterialCommunityIcons name={icon} size={20} color={iconColor} />}
      <Text style={[styles.itemLabel, { color: textColor }]}>{label}</Text>
      {right ? <View style={styles.itemRight}>{right}</View> : null}
      {tag ? (
        <View style={[styles.tag, { borderColor: colors.line }]}>
          <Text style={[styles.tagText, { color: colors.muted }]}>{tag}</Text>
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = {
  wrapper: {
    position: "relative",
  },
  card: {
    position: "absolute",
    top: HEADER.tabHeight + 8,
    borderWidth: 1,
    borderRadius: 12,
    padding: 6,
    shadowColor: "#000",
    shadowOpacity: 0.22,
    shadowOffset: { width: 0, height: 14 },
    shadowRadius: 40,
    elevation: 12,
    zIndex: 2147483647,
  },
  sectionTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    fontWeight: "600",
    paddingTop: 8,
    paddingHorizontal: 10,
    paddingBottom: 4,
  },
  divider: {
    height: 1,
    marginVertical: 6,
    marginHorizontal: 4,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  itemDisabled: {
    opacity: 0.5,
  },
  itemLabel: {
    fontSize: 14,
  },
  itemRight: {
    marginLeft: "auto",
  },
  tag: {
    marginLeft: "auto",
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  tagText: {
    fontSize: 11,
    textTransform: "uppercase",
  },
};
